import Notifications from '@/components/notifications/notifications';
import HeaderBackground from '@/components/ui/header-background/header-background';
import {
  ROUTE_ACTION_PLAN,
  ROUTE_LABS,
  ROUTE_DASHBOARD,
  ROUTE_HEALTH_DATA,
  ROUTE_RISK_ANALYSIS,
  ROUTE_RISK_TREND,
  ROUTE_TESTING_PLAN,
} from '@/constants/routes';
import useIsMobile from '@/hooks/use-is-mobile';
import { FeatureFlags, useFeatureFlags } from '@/services/feature-flag-provider';
import { getRoute } from '@/utils';
import React from 'react';
import DashboardNavigationBarDesktop from './components/dashboard-navigation-bar-desktop';
import DashboardNavigationBarMobile from './components/dashboard-navigation-bar-mobile';
import useDashboardNavigationBarController from './dashboard-navigation-bar.controller';
import { NavigationBarProps, NavigationMenuItem } from './types';

const defaultMenuItems: Array<NavigationMenuItem> = [
  { label: 'Dashboard', href: ROUTE_DASHBOARD, item: 'home' },
  { label: 'Risk Analysis', href: ROUTE_RISK_ANALYSIS, item: 'risk-analysis' },
  { label: 'Action Plan', href: getRoute(ROUTE_ACTION_PLAN), item: 'action-plan' },
  { label: 'Testing Plan', href: ROUTE_TESTING_PLAN, item: 'testing-plan' },
  { label: 'Health Data', href: getRoute(ROUTE_HEALTH_DATA), item: 'health-data' },
] as const;

function NavigationBar(navBarProps: NavigationBarProps) {
  const { variant = 'dark', ...restProps } = navBarProps;
  const { isEnabled } = useFeatureFlags();
  const isRiskTrendTabEnabled = isEnabled(FeatureFlags.IsRiskTrendTabEnabled);

  const additionalMenuItems: Array<NavigationMenuItem> = [
    ...(isRiskTrendTabEnabled ? [{ label: 'Risk Trend', href: ROUTE_RISK_TREND, item: 'risk-trend' } as const] : []),
    { label: 'Catch Labs', href: ROUTE_LABS, item: 'labs' } as const,
  ];

  const menuItems = [...defaultMenuItems, ...additionalMenuItems];

  const { name, email, handleLogout, navBarRef, notificationRef, isMobileMenuOpen, setIsMobileMenuOpen } = useDashboardNavigationBarController();

  const isMobile = useIsMobile();

  const props = { name, email, handleLogout, menuItems, variant, ...restProps };

  const dashboardNavBar = isMobile ? (
    <DashboardNavigationBarMobile {...props} navBarRef={navBarRef} isMobileMenuOpen={isMobileMenuOpen} setIsMobileMenuOpen={setIsMobileMenuOpen} />
  ) : (
    <DashboardNavigationBarDesktop {...props} navBarRef={navBarRef} />
  );

  return (
    <div className={`w-full sticky top-0 z-15 relative bg-[#0a0a0a] ${!isMobileMenuOpen ? 'overflow-hidden' : ''}`}>
      <HeaderBackground className="inset-0 -z-10" blur={40} opacity={.25}/>
      <Notifications notificationRef={notificationRef} />
      {dashboardNavBar}
    </div>
  );
}

export default React.memo(NavigationBar);
